import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Dispatch } from 'redux';

dayjs.extend(utc);

export const toEndOfDayISO = (dateStr: string): string => {
  const d = dayjs(dateStr);
  if (!d.isValid()) return dateStr;
  return d.utc().endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
};

// Helper function to split date range into 3-month chunks
export const splitDateRangeInto3Months = (
  startDate: string,
  endDate: string,
): Array<{ start: string; end: string }> => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const chunks: Array<{ start: string; end: string }> = [];

  let current = start.startOf('month');
  while (current.isBefore(end) || current.isSame(end, 'month')) {
    const chunkStart = current.isSame(start, 'month')
      ? start.format('YYYY-MM-DD')
      : current.startOf('month').format('YYYY-MM-DD');

    const chunkEndMonth = current.add(2, 'month');
    let chunkEnd: dayjs.Dayjs;

    if (chunkEndMonth.isAfter(end, 'month')) {
      chunkEnd = end;
    } else if (chunkEndMonth.isSame(end, 'month')) {
      chunkEnd = end;
    } else {
      chunkEnd = chunkEndMonth.endOf('month');
    }

    chunks.push({
      start: chunkStart,
      end: chunkEnd.format('YYYY-MM-DD'),
    });

    current = current.add(3, 'month');
  }

  return chunks;
};

export async function fetchWithDateChunking<TResponse, TMerged>(params: {
  startDate: string;
  endDate: string;
  thresholdMonths: number;
  apiFunction: (dispatch: Dispatch, queryParams: string) => Promise<TResponse>;
  mergeStrategy: (responses: TResponse[]) => TMerged;
  additionalParams?: Record<string, string | number | boolean>;
  dispatch: Dispatch;
}): Promise<TMerged> {
  const {
    startDate,
    endDate,
    thresholdMonths,
    apiFunction,
    mergeStrategy,
    additionalParams = {},
    dispatch,
  } = params;

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const monthsDiff = end.diff(start, 'month', true);

  if (monthsDiff > thresholdMonths) {
    const threeMonthChunks = splitDateRangeInto3Months(startDate, endDate);

    // Make parallel requests for each 3-month chunk
    const requests = threeMonthChunks.map((chunk) => {
      const params = new URLSearchParams();
      params.append('startDate', chunk.start);
      params.append('endDate', toEndOfDayISO(chunk.end));
      Object.entries(additionalParams).forEach(([key, value]) => {
        params.append(key, String(value));
      });
      return apiFunction(dispatch, params.toString());
    });

    const responses = await Promise.all(requests);

    // Apply merge strategy to combine all responses
    return mergeStrategy(responses);
  } else {
    // Single request for ranges within threshold
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', toEndOfDayISO(endDate));
    Object.entries(additionalParams).forEach(([key, value]) => {
      params.append(key, String(value));
    });
    const response = await apiFunction(dispatch, params.toString());

    // For single response, wrap it in an array and apply merge strategy
    // This ensures consistent return type
    return mergeStrategy([response]);
  }
}
